import { Checkbox, Form, FormField, Modal } from 'semantic-ui-react';
import { Footer } from './Footer';
import {
  ErrorsContextProvider,
  RHFormDatePicker,
  RHFormNumberInput,
  RHFormSelect,
  RHFormTimePicker,
  ValidationErrors,
} from 'components';
import { useEditLrwSession } from './useEditLrwSession';
import { BaseSyntheticEvent } from 'react';
import { Controller } from 'react-hook-form';
import { OnComputerLabel, OnPaperLabel } from '../details/OnComputerWritingFormatsBadges';
import { lrwSessionFormStyles as S } from '../common/lrw-session-form';

const formId = 'edit-lrw-session-form';

export const EditLrwSession = () => {
  const {
    form,
    generalErrors,
    venueOptions,
    isFetchingLrwSession,
    isSaving,
    handleSubmit,
    minEndTime,
    updateCapabilities,
    canToggleOnComputer,
    canToggleOnPaper,
  } = useEditLrwSession();

  const sessionEndTime = form.watch('sessionEndTime');

  return (
    <ErrorsContextProvider generalErrors={generalErrors}>
      <Modal open>
        <Modal.Header>Edit LRW session</Modal.Header>
        <Modal.Content>
          <Form
            id={formId}
            loading={isFetchingLrwSession || isSaving}
            error={generalErrors.length > 0}
            noValidate
            onSubmit={(e: BaseSyntheticEvent) => {
              form.handleSubmit(handleSubmit)(e);
            }}
          >
            <ValidationErrors />
            <S.ModuleBadge />
            <RHFormDatePicker name="sessionDate" control={form.control} label="Date" disabled />
            <S.TimePickersDiv>
              <RHFormTimePicker
                name="sessionStartTime"
                icon="clock outline"
                control={form.control}
                label="Start time"
                precision={30}
                disabled={!updateCapabilities?.canUpdateTimeBlock}
                onChange={(value) => {
                  if (value && sessionEndTime && value >= sessionEndTime) {
                    form.setValue('sessionEndTime', '');
                  }
                }}
                data-testid="session-start-time"
              />
              <RHFormTimePicker
                name="sessionEndTime"
                icon="clock outline"
                control={form.control}
                label="End time"
                precision={30}
                min={minEndTime}
                max="24:00"
                disabled={!updateCapabilities?.canUpdateTimeBlock}
                extraValues={[
                  {
                    hours: 24,
                    minutes: 0,
                  },
                ]}
                data-testid="session-end-time"
              />
            </S.TimePickersDiv>
            <RHFormSelect
              name="venueId"
              control={form.control}
              label="Venue"
              options={venueOptions}
              disabled
              openOnFocus
              minCharacters={0}
            />
            <FormField>
              <S.FieldGroupLabel id="writing-format-group">Writing format</S.FieldGroupLabel>
              <S.CheckboxWithCustomLabel>
                <Controller
                  name="onComputerEnabled"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox
                      id="lrw-session-on-computer"
                      disabled={!canToggleOnComputer}
                      checked={field.value}
                      onChange={(_, data) => field.onChange(data.checked)}
                      aria-labelledby="writing-format-group"
                    />
                  )}
                />
                <label htmlFor="lrw-session-on-computer">
                  <OnComputerLabel>CD</OnComputerLabel>
                </label>
              </S.CheckboxWithCustomLabel>
              <S.CheckboxWithCustomLabel>
                <Controller
                  name="onPaperEnabled"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox
                      id="lrw-session-on-paper"
                      disabled={!canToggleOnPaper}
                      checked={field.value}
                      onChange={(_, data) => field.onChange(data.checked)}
                      aria-labelledby="writing-format-group"
                    />
                  )}
                />
                <label htmlFor="lrw-session-on-paper">
                  <OnPaperLabel>+P</OnPaperLabel>
                </label>
              </S.CheckboxWithCustomLabel>
            </FormField>
            <RHFormNumberInput
              label="Test taker admission time"
              name="testTakerAdmissionTimeMinutes"
              control={form.control}
              min={0}
              max={99}
              innerLabel={{ basic: true, content: 'min' }}
              innerLabelPosition="right"
            />
          </Form>
        </Modal.Content>
        <Footer isSaving={isSaving} formId={formId} />
      </Modal>
    </ErrorsContextProvider>
  );
};
